import React, { useEffect, useState } from 'react';
import CargomaticModal from 'components/v2/modal';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import Table from 'components/v2/Table';
import { getYards } from 'utils/YardAPIUtils';
import Spinner from 'components/ui/Spinner';
import { isNull } from 'lodash';
import moment from 'moment';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import { bulkYardsColumnWidths, createBulkAddYardsColumns, createBulkAddYardsHeaders, } from './constants';
const Loading = function () {
    return (React.createElement("div", { "data-testid": "bulkAddYardsLoader", className: "justify-center w-full flex flex-col" },
        React.createElement("div", { className: "align-middle" },
            React.createElement("span", { className: "text-5xl text-green" },
                React.createElement(Spinner, { showMessage: false })))));
};
const validateStops = (stops) => {
    return stops.every((stop) => {
        return (!isNull(stop.action) &&
            !isNull(stop.empty) &&
            !isNull(stop.equipments) &&
            stop.equipments.length &&
            !isNull(stop.shipment) &&
            !isNull(stop.off_site) &&
            !isNull(stop.window_start) &&
            !isNull(stop.window_end) &&
            !isNull(stop.yardId) &&
            !isNull(stop.location.address));
    });
};
const areOffsiteYardStopsValid = (offsiteYardStops) => {
    return offsiteYardStops.some((offsiteYardStop) => validateStops(offsiteYardStop.stops));
};
const preparePayload = (offsiteYardStops) => {
    return offsiteYardStops.filter((offsiteYardStop) => validateStops(offsiteYardStop.stops));
};
const prepareDraftPayload = (selectedShipments) => {
    return selectedShipments.map((shipment) => {
        const { id: shipmentId } = shipment;
        return {
            jobId: shipmentId,
            stops: [
                {
                    action: null,
                    equipments: [],
                    empty: true,
                    shipment: shipmentId,
                    off_site: true,
                    yardId: null,
                    window_start: null,
                    window_end: null,
                    location: {
                        name: 'hardcoded',
                        address: null,
                    },
                },
                {
                    action: null,
                    equipments: [],
                    empty: true,
                    shipment: shipmentId,
                    off_site: true,
                    yardId: null,
                    window_start: null,
                    window_end: null,
                    location: {
                        name: 'hardcoded',
                        address: null,
                    },
                },
            ],
        };
    });
};
function BulkAddYardsModal({ showModal, onHide, t, selectedShipmentsData }) {
    const [isValid, setIsValid] = useState(false);
    const [tableShipments, setTableShipments] = useState([]);
    const [yards, setYards] = useState(null);
    const [offsiteYardStops, setOffsiteYardStops] = useState(prepareDraftPayload(selectedShipmentsData));
    const updateOffsetYardStops = (index, field, value) => {
        let stops;
        const isStartField = field.includes('start');
        const isEndField = field.includes('end');
        const updateStops = (newData) => {
            stops = [
                Object.assign(Object.assign({}, offsiteYardStops[index].stops[0]), newData),
                Object.assign(Object.assign({}, offsiteYardStops[index].stops[1]), newData),
            ];
        };
        if (isStartField || isEndField) {
            const stopNumber = field.replace(/\D/g, '');
            const changeItemNumber = stopNumber - 1;
            const restItemNumber = 2 - stopNumber; // get 0 or 1
            stops = [];
            stops[changeItemNumber] = Object.assign(Object.assign({}, offsiteYardStops[index].stops[changeItemNumber]), { [`window_${isStartField ? 'start' : 'end'}`]: value });
            stops[restItemNumber] = Object.assign({}, offsiteYardStops[index].stops[restItemNumber]);
        }
        else if (field === 'intent') {
            const empty = value === 'deliver_empty_pickup_empty';
            stops = [
                Object.assign(Object.assign({}, offsiteYardStops[index].stops[0]), { action: 'deliver', empty }),
                Object.assign(Object.assign({}, offsiteYardStops[index].stops[1]), { action: 'pickup', empty }),
            ];
        }
        else if (field === 'yardId') {
            const { address } = yards[index].find((yard) => yard.id === value);
            const yardRelatedFields = {
                yardId: value,
                location: {
                    name: 'hardcoded',
                    address,
                },
            };
            updateStops(yardRelatedFields);
        }
        else if (field === 'equipments') {
            const equipments = [value];
            updateStops({ equipments });
        }
        setOffsiteYardStops([
            ...offsiteYardStops.slice(0, index),
            Object.assign(Object.assign({}, offsiteYardStops[index]), { stops }),
            ...offsiteYardStops.slice(index + 1),
        ]);
    };
    const actualizeData = (index, field, value) => {
        // update table model
        setTableShipments([
            ...tableShipments.slice(0, index),
            Object.assign(Object.assign({}, tableShipments[index]), { [field]: value }),
            ...tableShipments.slice(index + 1),
        ]);
        // update payload
        updateOffsetYardStops(index, field, value);
    };
    const fetchYards = async (sortBy, sortOrder, skip, limit, yardInput = { id: null, marketId: null, nameInternal: null, status: null }) => {
        return getYards(sortBy, sortOrder, skip, limit, yardInput);
    };
    useEffect(() => {
        const yardPromises = [];
        const marketIds = selectedShipmentsData.map((shipment) => shipment.market_id);
        marketIds.forEach((marketId) => {
            yardPromises.push(fetchYards('nameInternal', 'ascend', null, null, {
                id: null,
                marketId,
                nameInternal: null,
                status: 'active',
            }));
        });
        Promise.all(yardPromises).then((yardsResponse) => {
            setYards(yardsResponse.map((yardData) => yardData.data.getYards.yards));
        });
    }, []);
    useEffect(() => {
        if (yards) {
            const shipmentsData = selectedShipmentsData.map((shipment, i) => {
                const { id, shipment_id, equipments } = shipment;
                return {
                    id,
                    shipment_id,
                    equipmentsArr: equipments,
                    yards: yards[i],
                };
            });
            setTableShipments([...shipmentsData]);
        }
    }, [yards]);
    useEffect(() => {
        setIsValid(areOffsiteYardStopsValid(offsiteYardStops));
    }, [offsiteYardStops]);
    function handleClose() {
        onHide();
    }
    function handleContainerChange(row) {
        return function (selected) {
            const index = tableShipments.findIndex((item) => item.id === row.id);
            actualizeData(index, 'equipments', selected.id);
        };
    }
    function handleYardChange(row) {
        return function (selected) {
            const index = tableShipments.findIndex((item) => item.id === row.id);
            actualizeData(index, 'yardId', selected.id);
        };
    }
    function handleIntentChange(row) {
        return function (selected) {
            const index = tableShipments.findIndex((item) => item.id === row.id);
            actualizeData(index, 'intent', selected.id);
        };
    }
    function handleStopChange(payload) {
        return function (selected) {
            const { type, row } = payload;
            const index = tableShipments.findIndex((item) => item.id === row.id);
            const value = selected ? moment(selected).format() : moment();
            actualizeData(index, type, value);
        };
    }
    async function handleSave() {
        ShipmentsActionCreators.addBulkYardStops(preparePayload(offsiteYardStops));
    }
    function getFooter() {
        return (React.createElement("div", { "data-testid": "bulkAddYardsFooter", className: "flex justify-end button-group" },
            React.createElement("button", { "data-testid": "bulkAddYardsCancel", className: "block button secondary-action", onClick: handleClose }, titleize(t('CANCEL'))),
            React.createElement("button", { "data-testid": "bulkAddYardsSave", className: "button button-green flex items-center", disabled: !isValid, onClick: handleSave },
                React.createElement("span", null, t('SAVE')))));
    }
    function getBody() {
        if (yards) {
            return (React.createElement(Table, { "data-testid": "bulkAddYardsTable", headers: createBulkAddYardsHeaders(t), columns: createBulkAddYardsColumns(t, handleContainerChange, handleYardChange, handleIntentChange, handleStopChange), data: tableShipments, tableClassName: "table-fixed block", trClassName: "flex w-full items-center", bodyClassName: "block overflow-y-scroll max-h-[710px] min-h-[130px]", headerClassName: "block px-3", colWidths: bulkYardsColumnWidths }));
        }
        return React.createElement(Loading, null);
    }
    return (React.createElement(CargomaticModal, { "data-testid": "bulkAddYardsModal", dialogClassName: "modal-override-large", title: t('BULK_ADD_YARD_STOPS'), style: { top: '20%' }, show: showModal, onHide: handleClose, body: getBody(), footer: getFooter() }));
}
export default withTranslation()(BulkAddYardsModal);
